import { Component, OnInit, Input, Output, EventEmitter, ViewChild, NgZone, AfterViewInit, inject, signal } from '@angular/core';
import { UnsubscriberClass, IAction, ActionType, StoreLoader, CharPadderPipe, LoaderSpinnerComponent, scrollToTop } from '@aston/foundation';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import * as AppStoreSelectors from 'apps/middle/src/app/root-store/app-store/selectors';
import * as AppStoreActions from 'apps/middle/src/app/root-store/app-store/actions';
import { UserClearanceLevel } from 'apps/middle/src/app/authentication-module/enums';
import { takeUntil, switchMap, map, startWith, filter, auditTime, take } from 'rxjs/operators';
import { UntypedFormBuilder } from '@angular/forms';
import { NgbNav, NgbNavModule, NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer } from '@angular/platform-browser';
import * as DunningActionDetailStoreActions from 'apps/middle/src/app/root-store/dunning-action-detail-store/actions';
import * as DunningActionDetailStoreSelectors from 'apps/middle/src/app/root-store/dunning-action-detail-store/selectors';
import { SuperDebtorDetailService } from 'apps/middle/src/app/debtors-module/services';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { IDebtorContact, ISuperDebtorIdentity } from 'apps/middle/src/app/debtors-module/models';
import { ICommentItem } from 'apps/middle/src/app/shared-module/models';
import { AsyncPipe } from '@angular/common';

import { DunningActionAttachmentsSelectionComponent } from '../dunning-action-attachments-selection/dunning-action-attachments-selection.component';
import { DunningActionCommentsComponent } from '../dunning-action-comments/dunning-action-comments.component';
import { createDunningActionAttachmentFromFile, mapToDisplayedContact, sortContacts } from '../../functions';
import { DunningActionFormComponent } from '../dunning-action-form/dunning-action-form.component';
import { DunningActionTypePipe } from '../../../shared-module/pipes/dunning-action-type.pipe';
import { SuperDebtorPageStore } from '../../../debtors-module/stores/super-debtor-page';
import { DunningActionSpec } from '../../classes/action-specs/action-spec.class';
import { IDunningActionAttachmentDocument } from '../../models';
import { DunningActionsSpecsService } from '../../services';
import { ActionModalPanel } from '../../enums';
import { DocumentType } from '../../../shared-module/enums';


@Component({
	selector: 'dunning-action-modal',
	templateUrl: './dunning-action-modal.component.html',
	imports: [
		LoaderSpinnerComponent,
		NgbNavModule,
		TranslateModule,
		DunningActionFormComponent,
		DunningActionCommentsComponent,
		DunningActionAttachmentsSelectionComponent,
		NgbDropdownModule,
		CharPadderPipe,
		AsyncPipe,
		DunningActionTypePipe
	]
})
export class DunningActionModalComponent extends UnsubscriberClass implements OnInit, AfterViewInit {

	protected readonly zone = inject(NgZone);
	private formBuilder = inject(UntypedFormBuilder);
	private sanitizer = inject(DomSanitizer);
	private store = inject(Store);
	private translationService = inject(TranslateService);
	private superDebtorDetailService = inject(SuperDebtorDetailService);
	private dasService = inject(DunningActionsSpecsService);
	private sdPageStore = inject(SuperDebtorPageStore);

	@Input() isEditMode = false;

	@Output() resultEvent = new EventEmitter<IAction>();

	@ViewChild(NgbNav) tabset;
	activeTabId$ = new Subject<ActionModalPanel>();

	formPanel = ActionModalPanel.Form;
	attachmentsPanel = ActionModalPanel.Attachments;
	commentsPanel = ActionModalPanel.Comments;
	completionPanel = ActionModalPanel.Completion;
	selectedPanel: ActionModalPanel = this.formPanel;
	readOnly = false;

	loader = new StoreLoader(
		this.store.select(DunningActionDetailStoreSelectors.selectDunningActionIsLoading),
		this.store.select(DunningActionDetailStoreSelectors.selectDunningActionError),
	);

	formLoader = new StoreLoader(
		this.store.select(DunningActionDetailStoreSelectors.selectFormIsLoading),
		this.store.select(DunningActionDetailStoreSelectors.selectFormError),
	);

	comments$ = this.store.select(DunningActionDetailStoreSelectors.selectCommentsAsList);

	notifiableUserNames$: Observable<string[]> = this.store.select(DunningActionDetailStoreSelectors.selectNotifiablesUsersDisplay).pipe(
		map(notifiables => notifiables.map(u => u.displayableTag)));

	dunningAction$ = this.store.select(DunningActionDetailStoreSelectors.selectDunningAction);
	dunningActionIsDone$ = this.store.select(DunningActionDetailStoreSelectors.selectIsDone);
	dunningActionSpec$: Observable<DunningActionSpec> = combineLatest([ this.dunningAction$, this.dasService.getSpecsStream() ]).pipe(
		filter(([action]) => !!action),
		map(([action, specs]) => specs[action.type]));

	commentsLoader = new StoreLoader(
		this.store.select(DunningActionDetailStoreSelectors.selectCommentsIsLoading),
		this.store.select(DunningActionDetailStoreSelectors.selectCommentsError),
	);

	attachmentTabHidden$ = this.dunningActionSpec$.pipe(map(spec => spec?.hideEditionAttachments), startWith(false));
	attachmentList$ = this.store.select(DunningActionDetailStoreSelectors.selectAttachments);
	attachmentLoader = new StoreLoader(
		this.store.select(DunningActionDetailStoreSelectors.selectAttachmentsIsLoading),
		this.store.select(DunningActionDetailStoreSelectors.selectAttachmentsError),
	);

	pcContacts$ = (this.sdPageStore.identityAsObservable() as Observable<ISuperDebtorIdentity>).pipe(
		switchMap(superDebtorIdentity => superDebtorIdentity.payingCenterId ?
			this.superDebtorDetailService.getSuperDebtorContactsForAction(superDebtorIdentity.payingCenterId) :
			of([])
		));
	sdContacts$ = this.store.select(DunningActionDetailStoreSelectors.selectContacts);

	actionsContacts$ = combineLatest([
		this.pcContacts$,
		this.sdContacts$,
		this.dunningAction$.pipe(map(da => (da.secondaryContactIds || []).concat(da.contacts))),
	]).pipe(
		map(([pcContacts, sdContacts, ids]) => ([ ...pcContacts, ...sdContacts ] as IDebtorContact[])
			.filter(c => ids.includes(c.id))
			.map(c => mapToDisplayedContact(this.translationService, c))
			.sort(sortContacts)
		));

	formIsDisabled: boolean;
	formIsDirty: boolean;
	form = this.formBuilder.group({});
	navigationDisabled = signal(true);
	actionsDisabled = signal(true);

	hasDunningClearanceLevel = false;
	hasDunningMassActionClearanceLevel = false;

	canBeCompleted$ = this.store.select(DunningActionDetailStoreSelectors.selectCanBeCompleted);

	sideEvent$ = new BehaviorSubject<boolean>(false);

	get accountingField() {
		return this.form.get('accounting')
	}

	ngOnInit() {
		this.store.select(DunningActionDetailStoreSelectors.selectIsEditMode)
			 .pipe(takeUntil(this.destroySubscriptions$))
			 .subscribe(isEditMode => this.isEditMode = isEditMode);

		combineLatest([
			this.dunningActionIsDone$,
			this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.Dunning)),
		]).pipe(
			map(([actionIsDone, allowed]) => actionIsDone || !allowed || this.readOnly),
			takeUntil(this.destroySubscriptions$))
		 .subscribe(readOnly => {
			 if (readOnly) {
				 this.form.disable()
			 } else {
				 this.form.enable()
			 }
			 this.formIsDisabled = readOnly || this.readOnly;
			 this.updateFormDisability();
		 });

		combineLatest([
			this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.Dunning)),
			this.store.select(AppStoreSelectors.selectClearsLevel(UserClearanceLevel.DunningMassAction))
		])
			.pipe(takeUntil(this.destroySubscriptions$))
			.subscribe(([dunningAllowed, dunningMassActionClearanceLevelAllowed]) => {
				this.hasDunningClearanceLevel = dunningAllowed;
				this.hasDunningMassActionClearanceLevel = dunningMassActionClearanceLevelAllowed;
			});
	}

	ngAfterViewInit() {

		// Lock submit buttons while the form is loading or invalid or untouched
		// Bug 24309: [Sauvegarder] reste inactif si on clique sur un autre onglet pendant le chargement de l'ar
		// Bug 36898: [Sauvegarder] est désactivé pour une ar dupliquée
		this.disabledState$(() => this.form.get('id')?.value && this.form.untouched).subscribe(disabled => this.actionsDisabled.set(disabled))

		// Lock navigation while the form is loading or invalid
		// Bug 36590: [Sauvegarder] est toujours actif sans toucher au formulaire
		this.disabledState$().subscribe(disabled => this.navigationDisabled.set(disabled))
	}

	disabledState$(shouldDisable: () => boolean = () => false) {
		return combineLatest([
			this.canBeCompleted$,
			this.loader.isLoading$,
			this.sideEvent$,
			this.dunningActionSpec$,
			this.form.events,
			this.activeTabId$.pipe(startWith(null)),
		]).pipe(
			auditTime(100),
			map(([canBeCompleted, isLoading, sideInfosChanged, spec]) =>
				   !this.formIsDisabled
				&& !sideInfosChanged
				&& (shouldDisable()
					|| isLoading
					|| this.form.invalid
					|| !canBeCompleted?.globalResult
					|| (!this.accountingField.value?.length && !spec.allowEmptyAccountingSelection(this.form.value))
				)
			),
			takeUntil(this.destroySubscriptions$),
		)
	}

	updateFormDisability() {
		Object.values(this.form.controls)
			.forEach(ctrl => this.form.disabled ? ctrl.disable({emitEvent: false}) : ctrl.enable({emitEvent: false}));
	}

	setPanel() {
		this.tabset.select('dunning-action-modal-tab-' + this.selectedPanel);
		this.activeTabId$.next(this.selectedPanel);
	}

	setAsDirty() {
		this.formIsDirty = true;
	}

	onSubmit(chainWithCompletion = false) {
		if (this.form.disabled) {
			return this.onClose();
		}
		// caught in DunningActionDetailStoreEffects::onFormOpen$
		this.resultEvent.emit({type: ActionType.SUBMIT, value: chainWithCompletion});
	}

	onDelete() {
		if (this.form.disabled) {
			return this.onClose();
		}
		this.resultEvent.emit({type: ActionType.DELETE, value: undefined});
	}

	onCancel() {
		if (this.form.disabled) {
			return this.onClose();
		}
		// caught in DunningActionDetailStoreEffects::onFormOpen$
		this.resultEvent.emit({type: ActionType.CANCEL, value: {formIsDirty: this.formIsDirty}});
	}

	onClose() {
		// caught in DunningActionDetailStoreEffects::onFormOpen$
		this.resultEvent.emit({type: ActionType.CLOSE, value: undefined});
	}
	
	onNewComment(comment: ICommentItem) {
		this.sideEvent$.next(true);
		this.store.dispatch(DunningActionDetailStoreActions.AddCommentRequest({ param: comment }));
	}

	onChangeComment(comment) {
		this.sideEvent$.next(true);
		this.store.dispatch(DunningActionDetailStoreActions.UpdateCommentRequest({ param: comment }));
	}

	onDeleteComment(comment) {
		this.sideEvent$.next(true);
		this.store.dispatch(DunningActionDetailStoreActions.DeleteCommentRequest({ param: comment }))
	}

	onNewAttachment(files: FileList) {
		this.sideEvent$.next(true);
		// eslint-disable-next-line @typescript-eslint/prefer-for-of
		for (let index = 0; index < files.length; index++) {
			const file = files[index];
			const attachment = createDunningActionAttachmentFromFile(file, true);
			attachment.document.url = this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file));
			this.store.dispatch(DunningActionDetailStoreActions.AddAttachmentsRequest({param: attachment}));
		}
	}

	onDeleteAttachment(param: IDunningActionAttachmentDocument) {
		this.sideEvent$.next(true);
		// to-be-created action documents should be unselected instead
		// to-be-generated documents should be unselected instead. see Bug 38662: Un document pièce désélectionné ne réapparaît pas
		return (this.form.value?.id && param.document?.id > 0)
			? this.store.dispatch(DunningActionDetailStoreActions.DeleteAttachmentRequest({ param }))
			: this.onUnselectDocuments([param])
	}

	onSelectDocuments(param: IDunningActionAttachmentDocument[]) {
		this.sideEvent$.next(true);
		this.store.dispatch(DunningActionDetailStoreActions.SelectDocuments({ param }))
		setTimeout(() => this.updateFormFromStore()) // update features toggles
	}

	onUnselectDocuments(param: IDunningActionAttachmentDocument[]) {
		this.sideEvent$.next(true);
		// DunningActionDocument should be deleted instead
		param.forEach(value => (this.form.value?.id && value.document?.id > 0 && value.document?.documentType === DocumentType.DunningActionDocument)
			? this.onDeleteAttachment(value)
			: this.store.dispatch(DunningActionDetailStoreActions.UnselectDocuments({ param }))
		)
		setTimeout(() => this.updateFormFromStore()) // update features toggles
	}

	updateFormFromStore() {
		this.dunningAction$.pipe(take(1)).subscribe(v => this.form.patchValue({
			bankStatementIncluded: v.bankStatementIncluded,
			paymentScheduleIncluded: v.paymentScheduleIncluded,
			accountStatementIncluded: v.accountStatementIncluded,
			statementChorusProIncluded: v.statementChorusProIncluded,
			accountingDocumentIncluded: v.accountingDocumentIncluded,
		}))
	}

	onTabChanged() {
		// let the tabset have time to switch
		setTimeout(_ => this.store.dispatch(AppStoreActions.Execute(() => scrollToTop('.dunning-action-modal .tab-content'))), 100);
	}
}
